import { useEffect } from "react";

/**
 * Runs the provided callback on a regular interval and in response
 * to browser visibility events. Useful for keeping things in sync.
 *
 * NOTE: Make sure to make your callback uses useCallback, is wrapped
 * in useMemo, or is otherwise setup so it doesn't change on every
 * render triggering the useEffect.
 *
 * @param callback - Function to execute on interval and visibility events
 * @param intervalMs - Interval in milliseconds between executions
 * @param runOnEffectFire - If true, executes callback immediately when the useEffect runs.
 *   This means onMount and on dependency changes.
 */
export function useOnVisibilityAndInterval({
  callback,
  intervalMs,
  runOnEffectFire,
}: {
  callback: () => void;
  intervalMs: number;
  runOnEffectFire: boolean;
}) {
  useEffect(() => {
    if (runOnEffectFire) {
      callback();
    }

    const intervalId = setInterval(callback, intervalMs);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        callback();
      }
    };

    window.addEventListener("focus", callback);
    window.addEventListener("pageshow", callback);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", callback);
      window.removeEventListener("pageshow", callback);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [runOnEffectFire, callback, intervalMs]);
}
